/**
 * Shared Status Color Styles
 *
 * SSoT for status badge/indicator colors across Plerity's frontend fleet —
 * the Astro SSR portal (CSS custom properties, brand-theme aware) and the
 * Tailwind v4 React SPA dashboard fleet (literal utility classes). These two
 * families render through fundamentally different mechanisms, so they stay
 * as separate exports rather than being forced onto one shape:
 *
 *   - `getStatusStyle()` / `PORTAL_STATUS_STYLES` — returns `{ bg, color }`
 *     CSS custom-property REFERENCES (`var(--success-bg)`, ...). These only
 *     resolve correctly in the portal app, whose `global.css` (and its
 *     per-org brand-theme derivation — see `@plerity/brand-theme`'s
 *     `brand-status.ts`) defines `--{success,warning,danger,info}-{bg,text}`
 *     and `--gray-{100,600}`. Do NOT use this flavor outside portal — the
 *     vars are undefined elsewhere and the result renders transparent.
 *     Promoted verbatim from `apps/portal/src/lib/status-styles.ts`; that
 *     file now re-exports this module (zero-churn shim).
 *
 *   - Domain-scoped `*_STATUS_CLASSES` records (below) — literal Tailwind
 *     utility class strings with `dark:` pairs, for the SPA dashboard fleet.
 *     Each was promoted from its original app-local `STATUS_STYLES` object.
 *     Where a key's exact class string is byte-identical either to one of
 *     StatusBadge's own 5 canonical severities (`STATUS_COLOR_CLASSES`,
 *     exported from `../StatusBadge`) or to another domain's literal value,
 *     it re-uses that shared constant instead of duplicating the string —
 *     true reconciliation. Everything else keeps its own literal entry
 *     because the source apps use slightly different shades/opacities (e.g.
 *     branded `success-*` tokens vs raw `green-*`, `/40` vs `/30` dark
 *     opacity, `-200` vs `-300`/`-400` dark text) that would visually shift
 *     if collapsed onto a different family — preserving exact colors takes
 *     priority over maximal dedup.
 *
 * @package @plerity/ui-core
 */

import { STATUS_COLOR_CLASSES } from "../StatusBadge/StatusBadge";

// ─── Portal (CSS custom properties, brand-theme aware) ─────────────────────

export type StatusStyleCssVars = { bg: string; color: string };

/**
 * Universal status colors for portal's CSS-var flavor — used when the
 * domain doesn't need specialized labels. Promoted verbatim from
 * `apps/portal/src/lib/status-styles.ts` (originally documented as
 * replacing duplicate STATUS_STYLES / statusColors in ClaimsList.tsx,
 * OfferingCard.tsx, ReferralList.tsx, NapReviewPanel.tsx).
 */
export const PORTAL_STATUS_STYLES: Record<string, StatusStyleCssVars> = {
  // Positive states
  active: { bg: "var(--success-bg)", color: "var(--success-text)" },
  verified: { bg: "var(--success-bg)", color: "var(--success-text)" },
  completed: { bg: "var(--success-bg)", color: "var(--success-text)" },
  converted: { bg: "var(--success-bg)", color: "var(--success-text)" },
  approved: { bg: "var(--success-bg)", color: "var(--success-text)" },

  // Informational states
  submitted: { bg: "var(--info-bg)", color: "var(--info-text)" },
  claimed: { bg: "var(--info-bg)", color: "var(--info-text)" },
  scheduled: { bg: "var(--info-bg)", color: "var(--info-text)" },
  fully_claimed: { bg: "var(--info-bg)", color: "var(--info-text)" },

  // Warning states
  paused: { bg: "var(--warning-bg)", color: "var(--warning-text)" },
  draft: { bg: "var(--warning-bg)", color: "var(--warning-text)" },
  contacted: { bg: "var(--warning-bg)", color: "var(--warning-text)" },
  pending: { bg: "var(--warning-bg)", color: "var(--warning-text)" },

  // Danger states
  cancelled: { bg: "var(--danger-bg)", color: "var(--danger-text)" },
  lost: { bg: "var(--danger-bg)", color: "var(--danger-text)" },
  rejected: { bg: "var(--danger-bg)", color: "var(--danger-text)" },

  // Neutral states
  expired: { bg: "var(--gray-100)", color: "var(--gray-600)" },
  unknown: { bg: "var(--gray-100)", color: "var(--gray-600)" },
};

/** Get portal's CSS-var status style with fallback to neutral. Portal-only — see module doc. */
export function getStatusStyle(status: string): StatusStyleCssVars {
  return PORTAL_STATUS_STYLES[status] ?? PORTAL_STATUS_STYLES.unknown;
}

// ─── Portal: NAP directory consistency status (LocalPresenceDeliverables.tsx) ─
//
// A literal-Tailwind-class straggler inside portal itself — pre-dates
// `PORTAL_STATUS_STYLES` above and used a different (non-CSS-var) rendering
// mechanism, so it's promoted as its own domain map rather than folded into
// the CSS-var flavor. Shades don't match any canonical severity exactly
// (bg-*-50/text-*-700 here vs StatusBadge's bg-*-100/text-*-800), so kept
// as its own literal entries — no reuse available without a color shift.

/**
 * Promoted verbatim from
 * apps/portal/src/components/react/local-presence/LocalPresenceDeliverables.tsx
 * (`NAP_STATUS_STYLES`).
 */
export const PORTAL_NAP_DIRECTORY_STATUS_CLASSES: Record<string, string> = {
  consistent: "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  corrected: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  mismatch: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

// ─── SMS: shared 4-severity palette (branded tokens, /40 dark opacity) ─────
//
// Identical byte-for-byte across all three sms call sites this was promoted
// from (Automations.tsx, A2PStatusPanel.tsx, AutomationStats.tsx) — defined
// once here and referenced by each domain map below instead of repeating.

export const SMS_STATUS_SEVERITY_CLASSES: Record<"success" | "warning" | "danger" | "neutral", string> = {
  success: "bg-success-100 text-success-800 dark:bg-success-900/40 dark:text-success-300",
  warning: "bg-warning-100 text-warning-800 dark:bg-warning-900/40 dark:text-warning-300",
  danger: "bg-danger-100 text-danger-800 dark:bg-danger-900/40 dark:text-danger-300",
  neutral: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

/** Promoted verbatim from apps/sms/src/routes/sms/Automations.tsx. */
export const SMS_AUTOMATION_STATUS_CLASSES: Record<string, string> = {
  active: SMS_STATUS_SEVERITY_CLASSES.success,
  paused: SMS_STATUS_SEVERITY_CLASSES.warning,
  draft: SMS_STATUS_SEVERITY_CLASSES.neutral,
};

/** Promoted verbatim from apps/sms/src/routes/sms/_sections/compliance/A2PStatusPanel.tsx. */
export const SMS_A2P_STATUS_CLASSES: Record<string, string> = {
  approved: SMS_STATUS_SEVERITY_CLASSES.success,
  pending: SMS_STATUS_SEVERITY_CLASSES.warning,
  rejected: SMS_STATUS_SEVERITY_CLASSES.danger,
  not_started: SMS_STATUS_SEVERITY_CLASSES.neutral,
  unknown: SMS_STATUS_SEVERITY_CLASSES.neutral,
};

/** Promoted verbatim from apps/sms/src/routes/sms/AutomationStats.tsx. */
export const SMS_AUTOMATION_RUN_STATUS_CLASSES: Record<string, string> = {
  success: SMS_STATUS_SEVERITY_CLASSES.success,
  failed: SMS_STATUS_SEVERITY_CLASSES.danger,
  skipped: SMS_STATUS_SEVERITY_CLASSES.neutral,
};

// ─── Ops: SOP execution step status (ExecutionRunner.tsx) ──────────────────

/**
 * Promoted verbatim (colors only — icon + label stay local to
 * ExecutionRunner.tsx; ui-core has no lucide-react dependency) from
 * apps/ops/src/routes/execution/ExecutionRunner.tsx. `in_progress`
 * intentionally uses the brand `primary-*` family rather than `info-*` — a
 * deliberate ops divergence (in-progress reads as "your brand color", not
 * "informational blue") — left exactly as-is, not folded into a canonical
 * severity.
 */
export const OPS_EXECUTION_STEP_STATUS_CLASSES: Record<string, string> = {
  completed: "bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300",
  skipped: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  failed: "bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-300",
  in_progress: "bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300",
  pending: "bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300",
};

// ─── Ops: operator queue row (OperatorQueueRow.tsx) ─────────────────────────

/**
 * Promoted verbatim (colors only — labels stay local to
 * OperatorQueueRow.tsx) from
 * apps/ops/src/routes/operator/queue/_components/OperatorQueueRow.tsx.
 * `HUMAN_GATED_AUTO`'s purple has no equivalent among StatusBadge's 5
 * canonical severities — left exactly as-is rather than adding a 6th
 * severity to the shared component for one call site.
 */
export const OPS_SURFACE_TYPE_CLASSES: Record<string, string> = {
  HUMAN_SUPERVISED: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  HUMAN_GATED_AUTO: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
};

/** Promoted verbatim (colors only — labels stay local) from OperatorQueueRow.tsx. */
export const OPS_CHASE_STATUS_CLASSES: Record<string, string> = {
  fresh: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  sla_breached: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  escalated: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

// ─── Dev: generated-artifact freshness (FreshnessBadge.tsx) ────────────────

/**
 * Promoted from apps/dev/src/components/FreshnessBadge.tsx. `fresh` and
 * `old` were byte-identical to StatusBadge's own `success`/`error`
 * severities, so they reuse `STATUS_COLOR_CLASSES` directly (true
 * reconciliation, not just relocation); `stale` (amber, not the canonical
 * warning `yellow`) and `missing` (`text-gray-600` vs canonical
 * `text-gray-800`) keep their own exact values — close to, but not
 * identical to, the canonical warning/neutral severities, so folding them
 * in would be a small but real visual regression.
 */
export const DEV_FRESHNESS_STATUS_CLASSES: Record<string, string> = {
  fresh: STATUS_COLOR_CLASSES.success,
  stale: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  old: STATUS_COLOR_CLASSES.error,
  missing: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

/** Dot-indicator classes, promoted verbatim from FreshnessBadge.tsx. */
export const DEV_FRESHNESS_DOT_CLASSES: Record<string, string> = {
  fresh: "bg-green-500 dark:bg-green-400",
  stale: "bg-amber-500 dark:bg-amber-400",
  old: "bg-red-500 dark:bg-red-400",
  missing: "bg-gray-400 dark:bg-gray-500",
};
